import { push } from 'react-router-redux';

// constants

export const ADD_SEGMENT = 'ADD_SEGMENT';
export const CLEAR_SEGMENTS = 'CLEAR_SEGMENTS';
export const LINK_SEGMENTS = 'LINK_SEGMENTS';
export const PAUSE_FULL_CLIP = 'PAUSE_FULL_CLIP';
export const PLAY_FULL_CLIP = 'PLAY_FULL_CLIP';
export const STOP_FULL_CLIP = 'STOP_FULL_CLIP';
export const PLAY_BACKGROUND_TRACK = 'PLAY_BACKGROUND_TRACK';
export const STOP_BACKGROUND_TRACK = 'STOP_BACKGROUND_TRACK';
export const PLAY_TRANSITION_AUDIO = 'PLAY_TRANSITION_AUDIO';
export const STOP_TRANSITION_AUDIO = 'STOP_TRANSITION_AUDIO';
export const UPDATE_PLAYBACK_POSITION = 'UPDATE_PLAYBACK_POSITION';
export const UPDATE_SEEK_POSITION = 'UPDATE_SEEK_POSITION';
export const RECEIVE_BUFFER = 'RECEIVE_BUFFER';
export const RECEIVE_START_BOUNDARY = 'RECEIVE_START_BOUNDARY';
export const RECEIVE_STOP_BOUNDARY = 'RECEIVE_STOP_BOUNDARY';
export const RECEIVE_START_BOUNDARY_AND_MOVE =
  'RECEIVE_START_BOUNDARY_AND_MOVE';
export const RECEIVE_TITLE = 'RECEIVE_TITLE';
export const RECEIVE_WAVEFORM_DATA = 'RECEIVE_WAVEFORM_DATA';
export const SET_ACTIVE = 'SET_ACTIVE';
export const SET_FADE = 'SET_FADE';
export const SET_BACKGROUND_TRACK_URL = 'SET_BACKGROUND_TRACK_URL';
export const SET_TRANSITION_AUDIO_ID = 'SET_TRANSITION_AUDIO_ID';
export const REMOVE_SEGMENT = 'REMOVE_SEGMENT';
export const SET_SUBMITTED = 'SET_SUBMITTED';

export const DRAG_TYPES = {
  CLIP: 'CLIP',
  CLIP_BOUNDARY: 'CLIP_BOUNDARY',
  CURSOR: 'CURSOR',
  NEW_CLIP: 'NEW_CLIP',
};

export const CLIPPER_HEIGHT = 168;
export const CLIPPER_MARGIN = 10;
export const CURSOR_GUTTER = 40;
export const CLIP_BORDER = 13;
export const LABEL_GUTTER = 40;

export const MAX_LENGTH = 5 * 60; // 5 mins in seconds
export const MIN_LENGTH = 0.5;
const SNAP_ZONE_SIZE = 30; // pixels
const WAVEFORM_SELECTION_START_MINIMUM = 0.00001; // zero will not draw a selection...

const emptyArray = [];
const emptyObject = {};

const initialSegmentState = {
  backgroundTrackUrl: null,
  doFade: false,
  isPlaying: false,
  isActive: false,
  segmentKey: 0,
  start: 0,
  stop: MAX_LENGTH,
  title: '',
  published: false,
  linkedSegment: null,
  transitionAudioId: null,
};

// This format should match waveform API
const initialWaveformState = {
  data: emptyArray,
  currentPosition: 0,
};

const initialState = {
  buffer: null,
  isPlayingFullClip: false,
  backgroundTrackUrlPlaying: null,
  transitionAudioIdPlaying: null,
  playAnimationInterval: null,
  seekPosition: 0,
  playbackPosition: 0,
  playbackSource: null,
  segments: emptyArray,
  waveform: emptyObject,
  isSubmitted: false,
};

// reducers

function waveformReducer(state = initialWaveformState, action) {
  switch (action.type) {
    case RECEIVE_WAVEFORM_DATA: {
      const { data } = action;
      return {
        ...state,
        data,
      };
    }
    default:
      return state;
  }
}

function segmentReducer(state = initialSegmentState, action) {
  switch (action.type) {
    case ADD_SEGMENT: {
      const { segment } = action;
      return {
        ...state,
        ...segment,
      };
    }
    case RECEIVE_TITLE: {
      if (state.segmentKey !== action.segmentKey) {
        return state;
      }
      const { title } = action;
      return {
        ...state,
        title,
      };
    }
    case RECEIVE_START_BOUNDARY: {
      if (state.segmentKey !== action.segmentKey) {
        return state;
      }
      let { start } = action;
      start = positionToTime(start, action.rightBound);
      start = constrainTime(
        start,
        'start',
        state,
        action.maxLength,
        action.lastStopBound,
        Math.min(state.stop, action.rightBound)
      );
      return {
        ...state,
        start,
      };
    }
    case RECEIVE_STOP_BOUNDARY: {
      if (state.segmentKey !== action.segmentKey) {
        return state;
      }
      let { stop } = action;
      stop = positionToTime(stop, action.rightBound);
      stop = constrainTime(
        stop,
        'stop',
        state,
        action.maxLength,
        Math.max(state.start, 0),
        action.nextStartBound || action.rightBound
      );
      return {
        ...state,
        stop,
      };
    }
    case RECEIVE_START_BOUNDARY_AND_MOVE: {
      if (state.segmentKey !== action.segmentKey) {
        return state;
      }
      let { start } = action;
      const width = state.stop - state.start;
      start = positionToTime(start, action.rightBound);
      let stop = start + width;
      // simpler constraints
      if (start > state.start) {
        // bound by right
        stop =
          stop > (action.nextStartBound || action.rightBound)
            ? action.nextStartBound || action.rightBound
            : stop;
        const delta = stop - start - width;
        if (delta !== 0) {
          start = start + delta;
        }
      } else {
        // bound by left
        start =
          start > (action.lastStopBound || 0)
            ? start
            : action.lastStopBound || 0;
        stop = start + width;
      }
      return {
        ...state,
        start,
        stop,
      };
    }
    case LINK_SEGMENTS: {
      if (state.segmentKey !== action.segmentKey) {
        return state;
      }
      const { linkedSegment } = action;
      return {
        ...state,
        linkedSegment,
      };
    }
    // remove any references in remaining segments
    case REMOVE_SEGMENT: {
      const { segmentKey } = action;
      const { linkedSegment } = state;
      return {
        ...state,
        linkedSegment: segmentKey === linkedSegment ? null : linkedSegment,
      };
    }
    case SET_ACTIVE: {
      if (state.segmentKey !== action.segmentKey) {
        return {
          ...state,
          isActive: false,
        };
      }
      return {
        ...state,
        isActive: true,
      };
    }
    case SET_FADE: {
      if (state.segmentKey !== action.segmentKey) {
        return state;
      }
      const { doFade } = action;
      return {
        ...state,
        doFade,
      };
    }
    case SET_BACKGROUND_TRACK_URL: {
      if (state.segmentKey !== action.segmentKey) {
        return state;
      }
      const { backgroundTrackUrl } = action;
      return {
        ...state,
        backgroundTrackUrl,
      };
    }
    case SET_TRANSITION_AUDIO_ID: {
      if (state.segmentKey !== action.segmentKey) {
        return state;
      }
      const { transitionAudioId } = action;
      return {
        ...state,
        transitionAudioId,
      };
    }
    default:
      return state;
  }
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case RECEIVE_BUFFER: {
      const { buffer } = action;
      return {
        ...state,
        buffer,
      };
    }
    case ADD_SEGMENT: {
      const { segments } = state;
      // make a initial segment, 0.05 in, 0.2 width
      const { segment = { start: 0.05, stop: 0.2 } } = action;
      const { buffer } = state;
      const rightBound = buffer ? buffer.duration : MAX_LENGTH;
      const maxLength = Math.min(rightBound, MAX_LENGTH);
      let { start, stop } = segment;
      start = positionToTime(start, rightBound);
      stop = positionToTime(stop, rightBound);

      // constrain to surrounding segments
      if (buffer && segments.length) {
        let lastStopBound = getLastStopBound(stop, null, segments);
        if (lastStopBound > stop) {
          // re-constrain (you must be overlapping right
          lastStopBound = getLastStopBound(start, null, segments);
        }

        start = constrainTime(
          start,
          'start',
          { start, stop },
          maxLength,
          lastStopBound,
          rightBound
        );
        let nextStartBound = getNextStartBound(start, null, segments);
        // edge case where the next start bound is from a segment actually before the start
        nextStartBound =
          nextStartBound && nextStartBound > start
            ? nextStartBound
            : rightBound;
        stop = constrainTime(
          stop,
          'stop',
          { start, stop },
          maxLength,
          lastStopBound,
          nextStartBound
        );
      } else {
        // constrain to limits, e.g. zero to max length for 'select all'
        stop = constrainTime(
          stop,
          'stop',
          { start, stop },
          maxLength,
          0,
          rightBound
        );
      }

      const newSegment = {
        segmentKey: generateNewSegmentKey(segments),
        start,
        stop,
      };

      return {
        ...state,
        segments: [
          ...state.segments,
          segmentReducer(undefined, { ...action, segment: newSegment }),
        ],
      };
    }
    case CLEAR_SEGMENTS: {
      return {
        ...state,
        segments: emptyArray,
      };
    }
    case RECEIVE_STOP_BOUNDARY:
    case RECEIVE_START_BOUNDARY:
    case RECEIVE_START_BOUNDARY_AND_MOVE: {
      // TODO: abstract this duplicate logic to linkSegmentsByBoundary
      const { segmentKey } = action;
      const { buffer, segments } = state;
      const boundedAction = action;

      // fill in missing start/stop (boundary is either/or)
      let { start, stop } = action;
      const segment = segments.find(s => s.segmentKey === segmentKey);
      const otherSegments = segments.filter(s => s.segmentKey !== segmentKey);

      if (buffer) {
        boundedAction.maxLength = Math.min(buffer.duration, MAX_LENGTH);
        boundedAction.rightBound = buffer.duration;
        start = start ? positionToTime(start, buffer.duration) : segment.start;
        stop = stop ? positionToTime(stop, buffer.duration) : segment.stop;
        boundedAction.nextStartBound = getNextStartBound(
          start,
          segmentKey,
          otherSegments
        );
        boundedAction.lastStopBound = getLastStopBound(
          stop,
          segment.linkedSegment,
          otherSegments
        );
      }
      return {
        ...state,
        segments: segments.map(s => segmentReducer(s, boundedAction)),
      };
    }
    case LINK_SEGMENTS: {
      const { segments } = state;
      return {
        ...state,
        segments: segments.map(s => segmentReducer(s, action)),
      };
    }
    case RECEIVE_TITLE: {
      return {
        ...state,
        segments: state.segments.map(s => segmentReducer(s, action)),
      };
    }
    case SET_TRANSITION_AUDIO_ID:
    case SET_BACKGROUND_TRACK_URL:
    case SET_ACTIVE:
    case SET_FADE: {
      return {
        ...state,
        segments: state.segments.map(s => segmentReducer(s, action)),
      };
    }
    case PAUSE_FULL_CLIP: {
      const { playAnimationInterval } = state;
      const { context } = action;
      if (context) {
        context.suspend();
      }
      if (playAnimationInterval) {
        clearInterval(playAnimationInterval);
      }
      return {
        ...state,
        playAnimationInterval: null,
        isPlayingFullClip: false,
      };
    }
    case PLAY_FULL_CLIP: {
      const { playAnimationInterval, playbackSource } = action;
      return {
        ...state,
        playAnimationInterval,
        playbackSource,
        isPlayingFullClip: true,
      };
    }
    case STOP_FULL_CLIP: {
      const { playbackSource, playAnimationInterval, seekPosition } = state;

      if (playbackSource) {
        playbackSource.onended = null;
        playbackSource.stop();
      }

      if (playAnimationInterval) {
        clearInterval(playAnimationInterval);
      }
      return {
        ...state,
        isPlayingFullClip: false,
        playAnimationInterval: null,
        playbackSource: null,
        playbackPosition: seekPosition,
      };
    }
    case PLAY_BACKGROUND_TRACK: {
      const { playbackSource, backgroundTrackUrlPlaying } = action;
      return {
        ...state,
        playbackSource,
        backgroundTrackUrlPlaying,
      };
    }
    case STOP_BACKGROUND_TRACK: {
      const { playbackSource } = state;

      if (playbackSource) {
        playbackSource.onended = null;
        playbackSource.stop();
      }
      return {
        ...state,
        backgroundTrackUrlPlaying: null,
        playbackSource: null,
      };
    }
    case PLAY_TRANSITION_AUDIO: {
      const { transitionAudioIdPlaying, playbackSource } = action;
      return {
        ...state,
        playbackSource,
        transitionAudioIdPlaying,
      };
    }
    case STOP_TRANSITION_AUDIO: {
      const { playbackSource } = state;

      if (playbackSource) {
        playbackSource.onended = null;
        playbackSource.stop();
      }
      return {
        ...state,
        transitionAudioIdPlaying: null,
      };
    }
    case REMOVE_SEGMENT: {
      const { segmentKey } = action;
      const { segments } = state;
      return {
        ...state,
        segments: segments
          .filter(s => s.segmentKey !== segmentKey)
          .map(s => segmentReducer(s, action)),
      };
    }
    case UPDATE_SEEK_POSITION: {
      const { seekPosition } = action;
      return {
        ...state,
        seekPosition,
      };
    }
    case UPDATE_PLAYBACK_POSITION: {
      const { playbackPosition } = action;
      return {
        ...state,
        playbackPosition,
      };
    }
    case RECEIVE_WAVEFORM_DATA: {
      return {
        ...state,
        waveform: waveformReducer(state.waveform, action),
      };
    }
    case SET_SUBMITTED: {
      const { isSubmitted } = action;
      return {
        ...state,
        isSubmitted,
      };
    }
    default:
      return state;
  }
}

// action creators
export function updateStartBoundary(boundary) {
  return {
    type: RECEIVE_START_BOUNDARY,
    ...boundary,
  };
}
export function updateStopBoundary(boundary) {
  return {
    type: RECEIVE_STOP_BOUNDARY,
    ...boundary,
  };
}
export function updateStartAndMove(boundary) {
  return {
    type: RECEIVE_START_BOUNDARY_AND_MOVE,
    ...boundary,
  };
}

export function linkSegments(segmentKey, linkedSegment) {
  return {
    type: LINK_SEGMENTS,
    segmentKey,
    linkedSegment,
  };
}

export function receiveBuffer(buffer) {
  return {
    type: RECEIVE_BUFFER,
    buffer,
  };
}

export function receiveWaveformData(data) {
  return {
    type: RECEIVE_WAVEFORM_DATA,
    data,
  };
}

export function addSegment(segment) {
  return {
    type: ADD_SEGMENT,
    segment,
  };
}

export function updateTitle(title, segmentKey) {
  return {
    type: RECEIVE_TITLE,
    segmentKey,
    title,
  };
}

export function pauseFullClipWithContext(context) {
  return {
    type: PAUSE_FULL_CLIP,
    context,
  };
}

export function playFullClipWithSourceAndInterval(source, interval) {
  return {
    type: PLAY_FULL_CLIP,
    playbackSource: source,
    playAnimationInterval: interval,
  };
}

export function stopFullClip() {
  return {
    type: STOP_FULL_CLIP,
  };
}

export function playBackgroundTrackWithSourceAndUrl(
  playbackSource,
  backgroundTrackUrlPlaying
) {
  return {
    type: PLAY_BACKGROUND_TRACK,
    playbackSource,
    backgroundTrackUrlPlaying,
  };
}

export function stopBackgroundTrack() {
  return {
    type: STOP_BACKGROUND_TRACK,
  };
}

export function clearSegments() {
  return {
    type: CLEAR_SEGMENTS,
  };
}

export function removeSegment(segmentKey) {
  return {
    type: REMOVE_SEGMENT,
    segmentKey,
  };
}

export function updatePlaybackPosition(playbackPosition) {
  return {
    type: UPDATE_PLAYBACK_POSITION,
    playbackPosition,
  };
}

export function updateSeekPosition(seekPosition) {
  return {
    type: UPDATE_SEEK_POSITION,
    seekPosition,
  };
}

export function setFade(doFade, segmentKey) {
  return {
    type: SET_FADE,
    doFade,
    segmentKey,
  };
}

export function setActive(segmentKey) {
  return {
    type: SET_ACTIVE,
    isActive: true,
    segmentKey,
  };
}

export function setBackgroundTrack(segmentKey, backgroundTrackUrl) {
  return {
    type: SET_BACKGROUND_TRACK_URL,
    segmentKey,
    backgroundTrackUrl,
  };
}

export function setTransitionAudioId(segmentKey, transitionAudioId) {
  return {
    type: SET_TRANSITION_AUDIO_ID,
    segmentKey,
    transitionAudioId,
  };
}

export function playTransitionWithSourceAndId(
  playbackSource,
  transitionAudioIdPlaying
) {
  return {
    type: PLAY_TRANSITION_AUDIO,
    playbackSource,
    transitionAudioIdPlaying,
  };
}

export function stopTransitionAudio() {
  return {
    type: STOP_TRANSITION_AUDIO,
  };
}

// i.e. to the next step
export function setSubmitted(isSubmitted) {
  return {
    type: SET_SUBMITTED,
    isSubmitted,
  };
}

// async/thunk actions

// TODO: rename this entry point? kind of overloaded for every drop action
export function updateBoundary(boundary) {
  return (dispatch, getState) => {
    const { segments } = getState().clipSelection;
    const { newSegment, move, segmentKey, start, stop } = boundary;
    const segment = segments.find(s => s.segmentKey === segmentKey);

    const currentlyActiveSegment = segments.find(s => s.isActive);

    if (newSegment) {
      dispatch(addSegment(boundary));
      const { segments } = getState().clipSelection; // re-fetch state to get new last segment
      const lastAddedSegment = segments.find(
        s => s.segmentKey === getLargestSegmentKey(segments)
      );
      if (lastAddedSegment) {
        dispatch(seekFullClip(lastAddedSegment.start));
        dispatch(linkSegmentsByBoundary(lastAddedSegment)); // check if we have new links!
      }
      return;
    }

    if (move) {
      const otherSegments = segments.filter(s => s.segmentKey !== segmentKey);
      // moving full clip
      dispatch(unlinkSegment(segmentKey)); // first unlink the segment to free it
      const nextSegmentKey = getSegmentToHaveLink(segment.stop, otherSegments);
      if (nextSegmentKey !== null) {
        // and the next linked segment
        dispatch(unlinkSegment(nextSegmentKey));
      }
      dispatch(updateStartAndMove(boundary));
      return dispatch(linkSegmentsByBoundary(boundary)); // check if we have new links!
    }

    if (start) {
      dispatch(updateStartBoundary(boundary));
      if (segment.linkedSegment !== null) {
        // update linked stop
        return dispatch(
          updateStopBoundary({
            segmentKey: segment.linkedSegment,
            stop: boundary.start,
          })
        );
      }
    } else {
      dispatch(updateStopBoundary(boundary));
      const otherSegments = segments.filter(s => s.segmentKey !== segmentKey);
      const nextStartBound = getNextStartBound(
        segment.stop,
        segmentKey,
        otherSegments
      );
      const segmentToHaveLink =
        nextStartBound && segment.stop === nextStartBound
          ? getSegmentToHaveLink(nextStartBound, otherSegments)
          : null;
      if (segmentToHaveLink !== null) {
        // update linked start
        return dispatch(
          updateStartBoundary({
            ...boundary,
            segmentKey: segmentToHaveLink,
            start: boundary.stop,
          })
        );
      }
    }
    return dispatch(linkSegmentsByBoundary(boundary)); // check if we have new links!
  };
}

export function linkSegmentsByBoundary(boundary) {
  return (dispatch, getState) => {
    const { segmentKey } = boundary;
    const { segments } = getState().clipSelection;
    const segment = segments.find(s => s.segmentKey === segmentKey);
    const otherSegments = segments.filter(s => s.segmentKey !== segmentKey);
    const { start, stop } = segment;
    const lastStopBound = getLastStopBound(
      stop,
      segment.linkedSegment,
      otherSegments
    );
    const linkedSegmentForCurrentSegment =
      lastStopBound && start === lastStopBound
        ? getlinkedSegmentKey(lastStopBound, otherSegments)
        : null;
    const nextStartBound = getNextStartBound(start, segmentKey, otherSegments);
    const segmentToHaveLink =
      nextStartBound && stop === nextStartBound
        ? getSegmentToHaveLink(nextStartBound, otherSegments)
        : null;
    if (linkedSegmentForCurrentSegment !== null) {
      dispatch(linkSegments(segmentKey, linkedSegmentForCurrentSegment));
    }
    if (segmentToHaveLink !== null) {
      dispatch(linkSegments(segmentToHaveLink, segmentKey));
    }
  };
}

export function unlinkSegment(segmentKey, makeRoom = false) {
  return (dispatch, getState) => {
    const { segments, buffer } = getState().clipSelection;
    const { width } = getState().browser;
    const segment = segments.find(s => s.segmentKey === segmentKey);
    dispatch(linkSegments(segmentKey, null));

    // now pop the last segment back a little
    // TODO: bad to get browser state here?
    if (makeRoom) {
      const linkedSegment = segments.find(
        s => s.segmentKey === segment.linkedSegment
      );
      if (linkedSegment && buffer) {
        const { stop } = linkedSegment;
        const snapZoneAsPercent = SNAP_ZONE_SIZE / (width - 2 * CLIPPER_MARGIN);
        const duration = buffer.duration;
        dispatch(
          updateStopBoundary({
            ...linkedSegment,
            stop:
              (stop - positionToTime(snapZoneAsPercent, duration)) / duration,
          })
        );
      }
    }
  };
}

export function updateBuffer(buffer) {
  return (dispatch, getState) => {
    dispatch(receiveBuffer(buffer));
    dispatch(receiveWaveformData(createWaveformDataFromBuffer(buffer)));
  };
}

export function stopAllAudio() {
  return (dispatch, getState) => {
    dispatch(stopFullClip());
    dispatch(stopBackgroundTrack());
    dispatch(stopTransitionAudio());
  };
}

// shortcut for playing one clipped segment
export function previewSegment(segmentKey) {
  return (dispatch, getState) => {
    const {
      seekPosition,
      segments,
      backgroundTrackUrlPlaying,
    } = getState().clipSelection;
    const { isActive, start, doFade, backgroundTrackUrl } = segments.find(
      s => s.segmentKey === segmentKey
    );
    if (!isActive) {
      dispatch(seekFullClip(start));
      dispatch(stopFullClip());
    }
    if (backgroundTrackUrlPlaying) {
      dispatch(stopAllAudio());
    }
    // becomes play/pause button once seek has already happened
    return dispatch(playOrPauseFullClip(doFade, backgroundTrackUrl));
  };
}

export function playFullClip(doFade = false, backgroundTrackUrl = null) {
  return (dispatch, getState) => {
    const {
      clipper,
      clipSelection: {
        buffer,
        playbackSource,
        playbackPosition,
        seekPosition,
        segments,
      },
    } = getState();
    const { audioBufferCache } = clipper;
    const totalDuration = buffer.duration;

    // check for an active segment, and bound clip
    const activeSegment = segments.find(s => s.isActive);
    const stop = activeSegment ? activeSegment.stop : totalDuration;
    const trimmedBuffer = trimAndFade(
      clipper.context,
      buffer,
      seekPosition,
      stop,
      doFade,
      audioBufferCache[backgroundTrackUrl]
    );

    let currentPlayInterval;
    const INTERVAL = 1000 / 60;
    const intervalStartTime = Date.now();
    const startBufferMS = playbackPosition || seekPosition;

    const trimmedDuration = trimmedBuffer.duration;
    const waveformSelectionStart = seekPosition / totalDuration;
    const waveformSelectionEnd = stop / totalDuration;
    let source = null;

    if (!playbackSource) {
      // create new source
      source = getSourceFromBufferAndContext(
        trimmedBuffer,
        clipper.context,
        () => dispatch(stopAllAudio())
      );
    } else {
      // resume old source
      source = playbackSource;
    }

    currentPlayInterval = setInterval(() => {
      dispatch(
        updatePlaybackPosition(
          startBufferMS + (Date.now() - intervalStartTime) / 1000
        )
      );
    }, INTERVAL);
    clipper.context.resume();

    dispatch(playFullClipWithSourceAndInterval(source, currentPlayInterval));
  };
}
export function pauseFullClip() {
  return (dispatch, getState) => {
    const { context } = getState().clipper;
    return dispatch(pauseFullClipWithContext(context));
  };
}

export function playOrPauseFullClip(doFade, backgroundTrackUrl) {
  return (dispatch, getState) => {
    const { isPlayingFullClip } = getState().clipSelection;
    if (isPlayingFullClip) {
      return dispatch(pauseFullClip());
    }
    return dispatch(playFullClip(doFade, backgroundTrackUrl));
  };
}

export function seekFullClip(seekTime) {
  return (dispatch, getState) => {
    const { buffer, isPlayingFullClip, segments } = getState().clipSelection;

    // see if we are bounded
    const segmentKey = getActiveSegmentFromSeekTime(seekTime, segments);
    dispatch(setActive(segmentKey));

    // Note, update to the new seek BEFORE stopping audio, stopping audio resets playback to the last seek
    dispatch(updateSeekPosition(seekTime));
    dispatch(stopAllAudio());
    if (isPlayingFullClip) {
      // restores stopped playback
      dispatch(playFullClip());
    }
  };
}

export function seekFullClipFromPosition(seekPosition) {
  return (dispatch, getState) => {
    const { width } = getState().browser;
    const {
      buffer: { duration },
      segments,
    } = getState().clipSelection;

    // see if we should snap
    // TODO: convert the snap zone into a state as part of browser width?
    const seekTime = closestSegmentBoundaryToPosition(
      seekPosition,
      segments,
      duration,
      SNAP_ZONE_SIZE / (width - 2 * CLIPPER_MARGIN)
    );
    return dispatch(seekFullClip(seekTime));
  };
}

export function toggleFade(segmentKey) {
  return (dispatch, getState) => {
    const { segments } = getState().clipSelection;
    const { doFade } = segments.find(s => s.segmentKey === segmentKey);
    return dispatch(setFade(!doFade, segmentKey));
  };
}

export function selectAll() {
  return (dispatch, getState) => {
    const { buffer, segments } = getState().clipSelection;
    if (segments.length > 1) {
      return; // noop
    }
    if (segments.length) {
      dispatch(clearSegments());
    }
    // note: addSegment expects percent start/stop
    dispatch(
      addSegment({
        start: 0,
        stop: 1,
      })
    );
  };
}

export function previewBackgroundTrack(segmentKey) {
  return (dispatch, getState) => {
    const { segments } = getState().clipSelection;
    const { backgroundTrackUrl } = segments.find(
      s => s.segmentKey === segmentKey
    );
    return dispatch(playOrPauseBackgroundTrack(backgroundTrackUrl));
  };
}

// note technically this is a play or 'stop' but looks like a pause
export function playOrPauseBackgroundTrack(backgroundTrackUrl) {
  return (dispatch, getState) => {
    const { backgroundTrackUrlPlaying } = getState().clipSelection;
    if (
      backgroundTrackUrlPlaying &&
      backgroundTrackUrlPlaying === backgroundTrackUrl
    ) {
      // hit a pause icon
      return dispatch(stopBackgroundTrack());
    }
    dispatch(stopAllAudio());
    return dispatch(playBackgroundTrack(backgroundTrackUrl));
  };
}

export function playBackgroundTrack(backgroundTrackUrl) {
  return (dispatch, getState) => {
    const {
      clipper: { context, audioBufferCache },
    } = getState();
    const buffer = audioBufferCache[backgroundTrackUrl];
    if (buffer) {
      const totalDuration = buffer.duration;
      const trimmedBuffer = trimAndFade(context, buffer, 0, buffer.duration);
      const source = getSourceFromBufferAndContext(trimmedBuffer, context, () =>
        dispatch(stopAllAudio())
      );
      context.resume();
      dispatch(playBackgroundTrackWithSourceAndUrl(source, backgroundTrackUrl));
    }
  };
}

// important to stop playback so that next preview gets the background track incorporated
export function setBackgroundTrackAndStopPlayback(
  segmentKey,
  backgroundTrackUrl
) {
  return (dispatch, getState) => {
    dispatch(stopAllAudio());
    return dispatch(setBackgroundTrack(segmentKey, backgroundTrackUrl));
  };
}

export function previewTransitionAudio(transitionAudioId) {
  return (dispatch, getState) => {
    const { transitionAudioIdPlaying } = getState().clipSelection;
    if (
      transitionAudioIdPlaying &&
      transitionAudioIdPlaying === transitionAudioId
    ) {
      // hit a pause icon
      return dispatch(stopTransitionAudio());
    }
    dispatch(stopAllAudio());
    return dispatch(playTransitionAudio(transitionAudioId));
  };
}

// overlap with action creator
export function playTransitionAudio(transitionAudioId) {
  return (dispatch, getState) => {
    const {
      clipper: { context, audioBufferCache, transitions },
    } = getState();
    const { url } = transitions.find(t => transitionAudioId === t.audioId);
    const buffer = audioBufferCache[url];
    if (buffer) {
      const totalDuration = buffer.duration;
      const trimmedBuffer = trimAndFade(context, buffer, 0, buffer.duration);
      const source = getSourceFromBufferAndContext(trimmedBuffer, context, () =>
        dispatch(stopAllAudio())
      );
      context.resume();
      dispatch(playTransitionWithSourceAndId(source, transitionAudioId));
    }
  };
}

// submission to next step
export function areSegmentsValid() {
  return (dispatch, getState) => {
    const {
      clipSelection: { buffer, segments },
    } = getState();
    dispatch(setSubmitted(true));
    if (segments.length) {
      return true;
    }
    // nothing selected, but less than max length?
    if (buffer && buffer.duration && buffer.duration <= MAX_LENGTH) {
      // if so select everything, go to next step
      dispatch(selectAll());
      return true;
    }
    // redirect to auto-clip flow
    dispatch(push('/clipper/publish'));
  };
}

// misc

export function constrainTime(
  time,
  role,
  segment,
  _maxLength = MAX_LENGTH,
  leftBound = 0,
  rightBound
) {
  const maxLength = Math.min(_maxLength, MAX_LENGTH);
  if (role === 'start') {
    if (time < leftBound) {
      return Math.max(leftBound, rightBound - MAX_LENGTH);
    }
    if (segment.stop - time > maxLength) {
      return Math.max(segment.stop - maxLength, leftBound);
    }
    if (segment.stop - time < MIN_LENGTH) {
      return Math.max(segment.stop - MIN_LENGTH, leftBound);
    }
    if (time > segment.stop) {
      return Math.max(segment.stop - MIN_LENGTH, leftBound);
    }
  } else {
    if (time > rightBound) {
      return Math.min(rightBound, leftBound + MAX_LENGTH);
    }
    if (time - segment.start > maxLength) {
      return Math.min(segment.start + maxLength, rightBound);
    }
    if (time - segment.start < MIN_LENGTH) {
      return Math.min(segment.start + MIN_LENGTH, rightBound);
    }
    if (time < segment.start) {
      return Math.min(segment.start + MIN_LENGTH, rightBound);
    }
  }
  return time;
}

export function trimAndFade(
  context,
  buffer,
  start,
  stop,
  doFade = false,
  backgroundTrackBuffer = null
) {
  const trimDuration = stop - start;

  if (trimDuration === 0) {
    return;
  }

  const trimmedBuff = context.createBuffer(
    buffer.numberOfChannels,
    trimDuration * buffer.sampleRate,
    buffer.sampleRate
  );

  if (backgroundTrackBuffer || !buffer.copyFromChannel) {
    // also fall back to this if native `copyFromChannel` not supported
    // copy data and determine max volume
    const startPoint = Math.floor((start / buffer.duration) * buffer.length);
    let maxVolume = 0;
    for (let c = 0; c < buffer.numberOfChannels; c++) {
      const channel = buffer.getChannelData(c);
      const trimChannel = trimmedBuff.getChannelData(c);
      for (let d = 0; d < trimChannel.length; d++) {
        const val = channel[startPoint + d];

        trimChannel[d] = val;

        if (val > maxVolume) {
          maxVolume = val;
        }
      }
    }
    // Background music:
    const fadeRampLength = Math.min(2, trimDuration / 2) * buffer.sampleRate;
    const backgroundMusicMultiplier = 0.5 * maxVolume;
    const backgroundMusicBuffer = backgroundTrackBuffer;

    if (!backgroundMusicBuffer) {
      // TODO - Show error? Audio is not loaded
    } else {
      for (let c = 0; c < trimmedBuff.numberOfChannels; c++) {
        const channel = backgroundMusicBuffer.getChannelData(0);
        const trimChannel = trimmedBuff.getChannelData(c);

        const fadeStartPoint = trimChannel.length - fadeRampLength;
        for (let d = 0; d < trimChannel.length; d++) {
          let backgroundVal =
            channel[d % backgroundMusicBuffer.length] *
            backgroundMusicMultiplier;

          // Fade the end of the background track:
          if (d > fadeStartPoint) {
            const volAdjustment =
              Math.floor(((d - fadeStartPoint) / fadeRampLength) * 10) / 10;
            backgroundVal = backgroundVal * (1 - volAdjustment);
          }

          trimChannel[d] += backgroundVal;
        }
      }
    }
  } else {
    // simpler copy since we do not need to determine max volume
    for (let c = 0; c < trimmedBuff.numberOfChannels; c++) {
      const sampleStart = Math.max(start * buffer.sampleRate, 0);
      buffer.copyFromChannel(trimmedBuff.getChannelData(c), c, sampleStart);
    }
  }

  // Fade adjustments:
  if (doFade) {
    const fadeRampLength = Math.min(1, trimDuration / 2) * buffer.sampleRate;

    for (let c = 0; c < trimmedBuff.numberOfChannels; c++) {
      const trimChannel = trimmedBuff.getChannelData(c);
      for (let d = 0; d < fadeRampLength; d++) {
        const volAdjustment = Math.floor((d / fadeRampLength) * 10) / 10;

        trimChannel[d] *= volAdjustment;
        trimChannel[trimChannel.length - 1 - d] *= volAdjustment;
      }
    }
  }

  return trimmedBuff;
}

// What % of samples will be used for shortening the array:
const waveformMinimum = 0.04;

function createWaveformDataFromBuffer(buffer) {
  const leftChannel = buffer.getChannelData(0);

  let shortArray = shortenArray(
    leftChannel,
    getWaveformAverageOver(leftChannel.length)
  ).map(v => Math.abs(v));

  let maxVolume = 0.000000000000001;
  for (let i = 0; i < shortArray.length; i++) {
    if (shortArray[i] > maxVolume) {
      maxVolume = shortArray[i];
    }
  }

  // Reduce the size of the waveform and set a minimum value:
  shortArray = shortArray.map(
    a => a * ((1 - waveformMinimum) / maxVolume) + waveformMinimum
  );

  return shortArray;
}

function shortenArray(arr, len) {
  if (arr.length <= len) {
    return arr;
  }

  const newArr = [];

  const step = Math.ceil(arr.length / len); // min 1

  let currStep = [];
  let s = 0;

  let shorteningResolutionDenomenator = getShorteningResolutionDenominatorFromTotalLength(
    arr.length
  );
  shorteningResolutionDenomenator =
    arr.length / len < shorteningResolutionDenomenator
      ? null
      : shorteningResolutionDenomenator;

  for (let i = 0; i < arr.length; i++) {
    s++;

    if (shorteningResolutionDenomenator) {
      if (i % shorteningResolutionDenomenator !== 0) {
        continue;
      }
    }

    if (s > step) {
      newArr.push(avg(currStep));
      currStep = [];
      s = 0;
    }

    currStep.push(arr[i]);
  }

  if (currStep.length > 0) {
    newArr.push(avg(currStep));
  }

  return newArr;
}

function avg(arr) {
  return sum(arr) / arr.length;
}

function sum(arr) {
  return arr.reduce((a, b) => a + b, 0);
}

/**
 * This is a inverse of a 'sampling rate' for the input buffers, which we want to maximize
 * for very large files for performance reasons.
 */
const _shorteningResolutionDenomenator = 100;
function getShorteningResolutionDenominatorFromTotalLength(length) {
  return Math.min(
    Math.floor(length / 200000 / 100) * 100,
    _shorteningResolutionDenomenator
  );
}

/**
 * The number to average or 'smooth' over
 */
const _waveformAverageOver = 500;
function getWaveformAverageOver(length) {
  return Math.min(length / 100, _waveformAverageOver);
}

// smallest start position AFTER current start position (assume moving right bound)
// filters out by others linking to segment
function getNextStartBound(currentStartBoundary, segmentKey = null, segments) {
  let segmentsToRight = segments.filter(s => s.stop > currentStartBoundary);
  if (segmentKey !== null) {
    // filter out links
    segmentsToRight = segmentsToRight.filter(
      s => segmentKey !== s.linkedSegment
    );
  }
  if (!segmentsToRight.length) {
    return 0;
  }
  return segmentsToRight.map(s => s.start).reduce((acc, current) => {
    if (current > currentStartBoundary && (!acc || current < acc)) {
      return current;
    }
    return acc;
  });
}

// largest stop position BEFORE current stop position (assume moving left bound)
// filters out by linkedSegment
function getLastStopBound(currentStopBoundary, linkedSegment = null, segments) {
  let segmentsToLeft = segments.filter(s => s.start < currentStopBoundary);
  if (linkedSegment !== null) {
    // filter out links
    segmentsToLeft = segmentsToLeft.filter(s => linkedSegment !== s.segmentKey);
  }
  if (!segmentsToLeft.length) {
    return 0;
  }
  return segmentsToLeft.map(s => s.stop).reduce((acc, current) => {
    if (current < currentStopBoundary && (!acc || current > acc)) {
      return current;
    }
    return acc;
  });
}

// looks backward
function getlinkedSegmentKey(bound, segments) {
  if (!segments.length) {
    return null;
  }
  const segment = segments.find(s => s.stop === bound);
  return segment ? segment.segmentKey : null;
}

// looks forward
function getSegmentToHaveLink(bound, segments) {
  if (!segments.length) {
    return null;
  }
  const segment = segments.find(s => s.start === bound);
  return segment ? segment.segmentKey : null;
}

function getLargestSegmentKey(segments) {
  if (!segments.length) {
    return -1;
  }
  const largestSegmentKey = segments
    .map(s => s.segmentKey)
    .reduce((acc, current) => {
      if (current > acc) {
        return current;
      }
      return acc;
    }, 0);
  return largestSegmentKey;
}
function generateNewSegmentKey(segments) {
  return getLargestSegmentKey(segments) + 1;
}

function getSourceFromBufferAndContext(buffer, context, onEnded) {
  // create new source
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(context.destination);
  source.start(0);
  source.onended = onEnded;
  return source;
}

function getActiveSegmentFromSeekTime(seekTime, segments) {
  if (!segments.length) {
    return null;
  }
  const segment = segments.find(s => s.start <= seekTime && s.stop > seekTime);
  return segment ? segment.segmentKey : null;
}

function closestSegmentBoundaryToPosition(
  position,
  segments,
  duration,
  minDistance = 0
) {
  if (!segments.length) {
    return positionToTime(position, duration);
  }
  const closestBoundary = segments
    .map(s => ({
      time: s.start,
      distance: absDistanceToBoundary(position, s.start, duration),
    }))
    .reduce((acc, current) => {
      if (
        current.distance < minDistance &&
        (!acc || current.distance < acc.distance)
      ) {
        return current;
      }
      return acc;
    }, null);
  return closestBoundary
    ? closestBoundary.time
    : positionToTime(position, duration);
}

function absDistanceToBoundary(position, boundary, duration) {
  return Math.abs(position - timeToPosition(boundary, duration));
}

function timeToPosition(time, duration) {
  return time / duration;
}

export function positionToTime(position, duration) {
  return Math.min(1, Math.max(0, position)) * duration;
}
